/* eslint-disable no-plusplus */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import i18n from 'i18next';
import moment from 'moment';
import get from 'lodash/get';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { NavigationHeader } from 'components/NavigationHeader';
import {
  ANALYTICS_EVENT,
  ANALYTICS_PROPERTY,
  AVAILABLE_BRAND_FEATURES,
  ExperienceVersion,
  RecommendationsTriggeredBy,
} from '@kouto/types';
import getTotalPriceExcludingLast from 'utils/data/sessions/getTotalPriceExcludingLast';
import getUniqueDaySessions from 'utils/data/sessions/getUniqueDaySessions';
import { getSessionPrice } from 'components/helpers';

import { formatListingMedia } from 'utils/listings';
import ReviewsList from 'components/ReviewsList';
import { Wrapper } from 'components/theme/Wrapper';
import PictureModal from 'components/PictureModal';
import ModalGallery from 'components/ModalGallery';
import ToggleFeature from 'components/ToggleFeature';
import ErrorBoundary from 'components/ErrorBoundary';
import { SkeletonLine } from 'components/theme/Skeleton';
import { analyticsManager } from 'features/analytics';

import useBookableAddOns from 'api/listing/useBookableAddOns';
import useResourceOptions from 'api/experience/useResourceOptions';
import { hasResourceOptionsForParticipants } from 'utils/resourceSelections/resourceSelections';
import deriveNextBookingStep from 'utils/cart/deriveNextBookingStep';
import { BOOKING_MODES } from 'types/booking';
import { validateAccessCode } from 'actions/access-code';
import useSelectedParticipants from 'selectors/participants';
import { useAppState, useBrandId, useDispatch } from 'AppProvider';
import {
  checkNextAvailableDate,
  getAvailableDates,
  getAvailableSessions,
  getDateRangeAvailableSessions,
  getFirstAvailableDate,
  resetFirstAvailableDate,
} from 'actions/experience';
import {
  setTierParticipants,
  integrateSessionParticipants,
} from 'actions/participants';
import useCartItems from 'hooks/useCartItems';
import useCancellationPolicy from 'hooks/useCancellationPolicy';
import {
  formatDateForReq,
  isImageRatioGreaterOne,
  serializeParams,
  isSessionMultipleTiers,
} from 'utils';

import { useIsMobile } from 'WindowDimensionProvider';
import useCustomHistory from 'hooks/use-custom-history';
import useSearchQueryParams from 'hooks/use-search-params';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import { useIsNarrowView } from 'ContainerDimensionProvider';
import { VALIDATE_ACCESS_CODE_FAILURE } from 'types/access-code';
import { useTierParticipants } from 'hooks/use-selected-participants';
import {
  STATUS_FAILURE,
  STATUS_IDLE,
  STATUS_PENDING,
  STATUS_SUCCESS,
} from 'types/app';
import {
  BOOKING_AVAILABILITY_MODE_PRIVATE,
  TYPE_ONE_TIME,
  TYPE_RECURRING,
} from 'types/experience';
import useAvailableSeats from 'components/ExperienceView/hooks/useAvailableSeats';
import { useCart } from 'hooks/useCart';

import { usePageViewEvent } from 'hooks/usePageViewEvent';
import { IsBookableMapper } from 'features/analytics/services/analytics.helpers';
import { usePriceTierDataFromSelectedParticipants } from 'features/analytics/hooks/usePriceTierAnalyticsDataCollection';
import { useExperienceAnalyticsData } from 'features/analytics/hooks/useExperienceAnalyticsData';
import { formatDuration } from 'utils/formatDuration';
import { TruncatedText } from 'components/TruncatedText';

import LimitedTimeMessage from 'components/atoms/LimitedTime/LimitedTimeMessage';
import useLimitedTimeStatus, {
  TicketSalesState,
} from 'hooks/useLimitedTimeStatus';
import { LimitedTimeContainer } from 'components/atoms/LimitedTime';
import PromotersCard from 'components/molecules/PromotersCard/PromotersCard';
import useBrandById from 'api/brand/useBrandById';
import useListingV3 from 'api/listing/useListingV3/useListingV3';
import useGetEligibleProgram from 'api/brand/useGetEligibleProgram';
import isHyattEligible from 'utils/pointEligibility/isHyattEligible';
import { isFeatureActive } from 'utils/featureFlag';
import useGenerateListingRecommendations from 'hooks/generateListing/useGenerateListingRecommendations';
import useExchangeId from 'hooks/useExchangeId';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import NotFoundExperience from 'components/NotFoundExperience/NotFoundExperience';
import useCustomQuestions from 'api/experience/useCustomQuestions/useCustomQuestions';
import hasParticipantOrCustomQuestion from 'utils/customQuestions/hasParticipantOrCustomQuestion';
import EventMeta from './sub-components/EventMeta';

import * as SC from './styles';
import { ExperienceLocation } from './ExperienceLocation';
import ExperienceCancellationPolicy from './ExperienceCancellationPolicy';
import useStartTimes from './hooks/useStartTimes';
import useCartExperienceSession from './hooks/useCartExperienceSession';
import { ExperienceDescImage } from './sub-components/ExperienceDescImage/ExperienceDescImage';
import ExperienceHeader from './ExperienceHeader';
import ExperienceHost from './ExperienceHost';
import CalendarAvailability from './CalendarAvailability';
import OpenExperienceView from './OpenExperienceView';
import RangeCalendarAvailability from './RangeCalendarAvailability';
import MediaCarousel from '../MediaCarousel/MediaCarousel';
import ExperienceBookingSidebar from './ExperienceBookingSidebar';

/* eslint react/prop-types: 'off' */

const ExperienceView = () => {
  const isNarrowView = useIsNarrowView();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const [isSquare, setIsSquare] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const { searchParams } = useSearchQueryParams();
  const { t: translate } = useTranslation();
  const [accessCodeNumber, setAccessCodeNumber] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const { onAddToCart } = useCart();
  const isRequestToBookEnabled = isFeatureActive(
    AVAILABLE_BRAND_FEATURES.ENABLE_REQUEST_BOOKINGS,
  );
  const showAdvertisedPrice = isFeatureActive(
    AVAILABLE_BRAND_FEATURES.ENABLE_SHOW_ADVERTISED_AMOUNT,
  );
  // The current route's query params to carry onto the checkout/booking route.
  // Read from react-router's location so it is populated in every router mode;
  // window.location.hash is empty outside hash mode.
  const { search: currentSearch } = useLocation();
  const querySuffix = currentSearch || '';

  const [, setSelectedTierCustomers] = useTierParticipants();

  const [participantValidationMessage, setParticipantValidationMessage] =
    useState(null);

  const [dateRangeValidationMessage, setDateRangeValidationMessage] =
    useState(null);

  const onImgLoad = ({ target: img }) => {
    setIsSquare(isImageRatioGreaterOne(img.naturalWidth, img.naturalHeight));
  };
  const brandId = useBrandId();
  const { replace: customReplace } = useCustomHistory();
  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    setValue: setSidebarFormValue,
    watch,
    setError,
    clearErrors,
  } = useForm();

  const {
    availabilities: availableDates,
    sessions: availableSessions,
    firstAvailableDate,
    fetchFirstAvailabilityStatus,
    fetchedUntilDate,
    nextAvailableDate,
    searchAvailabilityStatus,
    experience,
    fetchExperienceStatus,
    fetchSessionStatus,
  } = useAppState((state) => state.experience);

  const { data: exchangeIds, error: exchangeIdError } = useExchangeId(
    experience?.id && !experience?.loading ? experience?.id : null,
    brandId,
  );
  const [listingId] = exchangeIds ?? [null];

  const languageCode = useSupportedLanguage();

  const {
    data: listing,
    isLoading: isLoadingListing,
    error: listingError,
  } = useListingV3({
    listingId,
    version:
      String(searchParams.latest).toLowerCase() === 'true'
        ? ExperienceVersion.LATEST
        : ExperienceVersion.PUBLISHED,
    ...(languageCode ? { language: languageCode } : {}),
  });

  const { data: customQuestions, isLoading: isLoadingCustomQuestions } =
    useCustomQuestions({
      experienceId: listing?.experienceId,
      brandId: listing?.brandId,
      skip: undefined,
      includeDefault: true,
      language: languageCode,
    });

  const { hasCustomQuestions, askParticipantsInfo } =
    hasParticipantOrCustomQuestion(customQuestions);

  const promoterBrand = useBrandById(listing?.providerBrandId ?? null);
  const { data: eligibleProgram } = useGetEligibleProgram(
    listing?.brandId,
    listing?.id && listing?.brandId && listing?.productLine
      ? {
          listings: [
            {
              id: listing?.id,
              brandId: listing?.brandId,
              productLine: listing?.productLine,
            },
          ],
        }
      : null,
  );

  const limitedTime = useLimitedTimeStatus(
    listing?.limitedTime?.ticketSalesOpenAt,
    listing?.limitedTime?.ticketSalesCloseAt,
  );

  const hideWidgets = limitedTime?.state === TicketSalesState.CLOSED;

  const { cancellationPolicy, isLoading: isLoadingCancellationPolicy } =
    useCancellationPolicy(experience.cancellationPolicyId);

  const durations = useMemo(() => {
    const durationsSet = new Set();
    return availableSessions
      .reduce((acc, session) => {
        const { duration } = session;
        // hide durations with no start times, missing durations, or duplicates
        if (
          !session.supportedParticipantsCount ||
          !duration ||
          durationsSet.has(duration)
        ) {
          return acc;
        }
        durationsSet.add(duration);
        acc.push({
          value: duration,
          label: formatDuration(duration),
          durationInMinutes: moment.duration(duration, 'minutes'),
        });

        return acc;
      }, [])
      .sort((a, b) => a.durationInMinutes - b.durationInMinutes);
  }, [availableSessions]);

  const { currentExperienceDetail } = useExperienceAnalyticsData();

  usePageViewEvent({
    eventName: ANALYTICS_EVENT.VIEW_EXPERIENCE_DETAIL,
    payload: {
      [ANALYTICS_PROPERTY.ExperienceId]: currentExperienceDetail?.id,
      [ANALYTICS_PROPERTY.ExperienceTitle]: currentExperienceDetail?.title,
    },
    optionalPayload: {
      [ANALYTICS_PROPERTY.IsBookable]:
        IsBookableMapper[
          (currentExperienceDetail?.isBookable ?? '').toString()
        ],
      [ANALYTICS_PROPERTY.ExperienceCategory]:
        currentExperienceDetail?.category?.name,
    },
    isNotReady: !currentExperienceDetail?.id,
  });

  const { cartItems } = useCartItems();

  const { isBookable, type } = experience;
  const isRecurring = isEqual(type, TYPE_RECURRING);
  const isOneTime = isEqual(type, TYPE_ONE_TIME);
  const isHostLed = !experience?.isNoHost;

  const isCartFeatureEnabled = useBrandToggleFeature('shoppingCart');

  const isLinkOutForHiltonEnabled = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.LINK_OUT_RECOMMENDATIONS,
  );
  const { generateRecommendations } = useGenerateListingRecommendations();
  const linkOutRecommendationTriggeredRef = useRef(null);

  useEffect(() => {
    const firstAvailableDateSettled =
      fetchFirstAvailabilityStatus === STATUS_SUCCESS ||
      fetchFirstAvailabilityStatus === STATUS_FAILURE;

    const userStart = searchParams?.startDate || searchParams?.from;
    const userEnd = searchParams?.endDate || searchParams?.to;
    const hasUserRange = !!(userStart && userEnd);
    const triggerKey = `${experience?.id ?? ''}|${userStart ?? ''}|${
      userEnd ?? ''
    }`;

    if (
      !isLinkOutForHiltonEnabled ||
      isBookable ||
      experience?.loading ||
      !experience?.id ||
      !experience?.externalField?.value ||
      !brandId ||
      !firstAvailableDateSettled ||
      linkOutRecommendationTriggeredRef.current === triggerKey
    ) {
      return;
    }

    linkOutRecommendationTriggeredRef.current = triggerKey;

    if (hasUserRange) {
      const startMs = new Date(userStart).getTime();
      const endMs = new Date(userEnd).getTime();
      const midpointDate = new Date((startMs + endMs) / 2);
      const halfSpanDays = Math.max(
        1,
        Math.round((endMs - startMs) / 2 / (1000 * 60 * 60 * 24)),
      );
      generateRecommendations({
        experienceIds: [experience.id],
        resourceGroupCollectionIds: [],
        sessionDate: midpointDate,
        dateRangeDays: halfSpanDays,
        brandId,
        triggeredBy: RecommendationsTriggeredBy.LINKOUT,
      });
      return;
    }

    generateRecommendations({
      experienceIds: [experience.id],
      resourceGroupCollectionIds: [],
      sessionDate: firstAvailableDate || new Date(),
      dateRangeDays: firstAvailableDate ? 2 : 365,
      brandId,
      triggeredBy: RecommendationsTriggeredBy.LINKOUT,
    });
  }, [
    brandId,
    experience?.externalField?.value,
    experience?.id,
    experience?.loading,
    fetchFirstAvailabilityStatus,
    firstAvailableDate,
    generateRecommendations,
    isBookable,
    isLinkOutForHiltonEnabled,
    searchParams?.endDate,
    searchParams?.startDate,
    searchParams?.from,
    searchParams?.to,
  ]);

  const { error: validateAccessCodeError, validateAccessCodeStatus } =
    useAppState((state) => state.accessCode);

  const {
    selectedParticipants,
    selectedSession,
    totalSelected: totalSelectedParticipants,
    currentExperience,
  } = useSelectedParticipants();

  const [singlePriceTierCount, setSinglePriceTierCount] = useState(1);

  const getPriceTierExperienceSelectedTiers = () => {
    if (experience.loading || !experience.priceTiers) return [];

    if (experience.priceTiers.length > 1) return selectedParticipants;

    return [
      {
        ...experience.priceTiers[0],
        selectedNumber: singlePriceTierCount,
      },
    ];
  };

  usePriceTierDataFromSelectedParticipants({
    title: experience?.title || '',
    category: experience?.category?.name,
    tiers: getPriceTierExperienceSelectedTiers(),
  });

  const isWithinCuttoff = selectedSession?.withinOnlineBookingCutoffWindow;

  const [showModal, setShowModal] = useState(false);
  const [isPrivateMode, setIsPrivateMode] = useState(false);
  const [selectedEventSession, setSelectedEventSession] = useState(null);
  const availablePriceTiers = selectedEventSession?.priceTiers || [];

  // initial date range should always be kept null at first
  // because the Availability calendar once rendered on a date doesn't change
  // month when changing the dateRange state.
  const [dateRange, setDateRange] = useState({
    startDateCalendar: null,
    endDateCalendar: null,
  });
  const [
    selectedSessionDateAvailabilityCalendar,
    setSelectedSessionDateAvailabilityCalendar,
  ] = useState(
    searchParams.sessionDate ? moment(searchParams.sessionDate) : null,
  );

  const { startDateCalendar, endDateCalendar } = dateRange;
  const [codeIsRequiredMsg, setCodeIsRequiredMsg] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const userProfileImage = get(experience, 'hostedBy.profilePicture.uri');
  const experienceDescImage = experience.descriptionPicture?.uri;
  const isExperienceFetchSuccess = fetchExperienceStatus === STATUS_SUCCESS;

  const guests = watch('noOfGuests');
  const formattedDate = formatDateForReq(
    selectedSessionDateAvailabilityCalendar,
  );

  const availableSeatsForSession = useAvailableSeats(
    selectedEventSession,
    searchParams.sessionDate ?? formattedDate,
    experience.id,
  );
  const [selectedDuration, setSelectedDuration] = useState(durations[0]?.value);
  const [selectedStartTime, setSelectedStartTime] = useState(null);
  // Set once the user manually picks a start time, so a ?sessionTime deep link stops overriding
  // their choice. Until then the deep-linked slot is (re)applied whenever it becomes available.
  const userSelectedStartTimeRef = useRef(false);
  const selectedSessionStartDateTime =
    (searchParams.sessionDate ?? formattedDate) && selectedStartTime
      ? `${searchParams.sessionDate ?? formattedDate}T${selectedStartTime}`
      : undefined;
  const hasBookableAddOns = useBookableAddOns({
    listingId: listing?.id,
    experienceId: experience?.id,
    startDateTime: selectedSessionStartDateTime,
    sessionDuration: selectedDuration,
    hasAddOns: !!listing?.addOns?.length,
  });
  const { getResourceOptions } = useResourceOptions({
    experienceId: experience?.id,
    startDateTime: selectedSessionStartDateTime,
    sessionDuration: selectedDuration,
    skip: !!experience?.loading,
  });

  // Wrap the setter handed to the sidebar so a manual pick flips the ref above. The selection
  // effect keeps setSelectedStartTime for its own programmatic updates (which must NOT flip it).
  const handleSelectStartTime = (startTime) => {
    userSelectedStartTimeRef.current = true;
    setSelectedStartTime(startTime);
  };

  const { experienceStartTimes, isUnavailable } = useStartTimes({
    selectedGuests: isSessionMultipleTiers(availablePriceTiers)
      ? totalSelectedParticipants
      : guests?.value,
    selectedDate: searchParams.sessionDate ?? formattedDate,
    availablePriceTiers: availablePriceTiers[0] ? [availablePriceTiers[0]] : [],
    selectedDuration,
    filterDisabled: true,
  });

  const isSoldOut = useMemo(() => {
    if (selectedSession) {
      return selectedSession?.supportedParticipantsCount === 0;
    }
    return (
      availableSessions.length &&
      availableSessions.every((sess) => sess.supportedParticipantsCount === 0)
    );
  }, [selectedSession, availableSessions]);

  const cartExperience = useCartExperienceSession(
    selectedEventSession,
    searchParams.sessionDate ?? formattedDate,
  );

  const dispatchAvailableSessionsHelper = (date, query) => {
    if (experience?.loading || !experience?.id) {
      return;
    }
    dispatch(
      getAvailableSessions(experience.id, formatDateForReq(date), {
        ...query,
        latest: String(searchParams.latest).toLowerCase() === 'true',
        cartExperienceList: cartItems,
      }),
    );
  };

  const getAvailableDatesHelper = ({
    from,
    to,
    isPrivate,
    shouldAppend,
    latest,
  }) => {
    if (experience?.loading || !experience?.id) {
      return {};
    }

    return dispatch(
      getAvailableDates(
        experience,
        {
          from: formatDateForReq(from),
          to: formatDateForReq(to),
          isPrivate,
          latest,
        },
        shouldAppend,
      ),
    );
  };

  useEffect(() => {
    if (validateAccessCodeError) {
      setAccessCodeNumber('');
    }
  }, [validateAccessCodeError]);

  // The classic list filters with `startDate`; the landing page Browse filter writes `from`. Either
  // one is where the guest wants to start, so the first available date is searched from there.
  const filterStartDate = searchParams?.startDate ?? searchParams?.from;

  useEffect(() => {
    if (!isExperienceFetchSuccess || !experience.id) return;
    setSidebarFormValue(
      'sessionDate',
      searchParams.sessionDate ? moment(searchParams.sessionDate) : moment(),
    );
    const startSearchDate =
      searchParams?.startDate && searchParams?.endDate
        ? moment(searchParams.startDate).startOf('month')
        : moment();

    let endSearchDate =
      searchParams?.startDate && searchParams?.endDate
        ? moment(searchParams.endDate).endOf('month')
        : moment().add(1, 'month');

    if (endSearchDate.isSame(startSearchDate, 'month')) {
      endSearchDate = startSearchDate.clone().add(1, 'month').endOf('month');
    }
    if (searchParams?.sessionDate) {
      setDateRange({
        startDateCalendar: moment(searchParams.sessionDate).startOf('month'),
        endDateCalendar: moment(searchParams.sessionDate)
          .endOf('month')
          .add(1, 'month')
          .endOf('month'),
      });
      dispatchAvailableSessionsHelper(moment(searchParams.sessionDate), {
        isPrivate: searchParams?.mode === BOOKING_MODES.PRIVATE,
        latest: String(searchParams.latest).toLowerCase() === 'true',
      });
      getAvailableDatesHelper({
        from: moment(searchParams.sessionDate).startOf('month'),
        to: moment(searchParams.sessionDate)
          .endOf('month')
          .add(1, 'month')
          .endOf('month'),
        isPrivate: isPrivateMode,
        shouldAppend: false,
        latest: String(searchParams.latest).toLowerCase() === 'true',
      });
      return;
    }
    dispatch(
      getFirstAvailableDate(experience.id, {
        from: filterStartDate
          ? formatDateForReq(filterStartDate)
          : formatDateForReq(moment()),
        isPrivate: isPrivateMode,
        latest: String(searchParams.latest).toLowerCase() === 'true',
      }),
    );
    if (searchParams?.accessCode) {
      setAccessCodeNumber(searchParams.accessCode);
    }
    /* eslint-disable consistent-return */
    return function cleanup() {
      dispatch(resetFirstAvailableDate());
    };
  }, [experience.id]);

  const handlePrivateCheckboxChange = () => {
    if (!experience?.id) return;

    const isPrivate = !isPrivateMode;
    setIsPrivateMode(!isPrivateMode);
    getAvailableDatesHelper({
      from: startDateCalendar,
      to: endDateCalendar,
      isPrivate,
      shouldAppend: true,
      latest: String(searchParams.latest).toLowerCase() === 'true',
    });

    dispatchAvailableSessionsHelper(selectedSessionDateAvailabilityCalendar, {
      isPrivate,
      latest: String(searchParams.latest).toLowerCase() === 'true',
    });

    dispatch(
      getFirstAvailableDate(experience.id, {
        from: filterStartDate
          ? formatDateForReq(filterStartDate)
          : formatDateForReq(moment()),
        isPrivate,
        latest: String(searchParams.latest).toLowerCase() === 'true',
      }),
    );
  };

  const handleBooking = async (data) => {
    const bookingMode =
      data?.isPrivateSession ||
      experience?.bookingAvailabilityMode === BOOKING_AVAILABILITY_MODE_PRIVATE
        ? BOOKING_MODES.PRIVATE
        : BOOKING_MODES.SHARED;

    const params = new URLSearchParams();
    if (String(searchParams.latest).toLowerCase() === 'true') {
      params.set('latest', 'true');
    }

    if (isRequestToBookEnabled) {
      const uniqueSessions = getUniqueDaySessions(availableSessions);

      // Validate that each night in the date range has an available session
      if (startDateCalendar && endDateCalendar) {
        const nights = endDateCalendar.diff(startDateCalendar, 'days');
        const missingDates = [];
        const day = startDateCalendar.clone();

        for (let i = 0; i < nights; i++) {
          const hasSessionForDay = uniqueSessions.some((session) =>
            moment(session.startDateTime).isSame(day, 'day'),
          );

          if (!hasSessionForDay) {
            missingDates.push(day.format('MMM D'));
          }

          day.add(1, 'day');
        }

        if (missingDates.length > 0) {
          const datesStr = missingDates.join(', ');
          setDateRangeValidationMessage(
            `${translate('noAvailableSessionsForSelectedDates')} (${datesStr})`,
          );
          return;
        }
      }

      // Clear validation message if we get here
      setDateRangeValidationMessage(null);

      const converted = uniqueSessions.map((session) => {
        const tier = session.priceTiers[0];
        return {
          ...tier,
          selectedNumber: 1,
        };
      });
      const cartData = {
        participants: (isSessionMultipleTiers(availablePriceTiers)
          ? selectedParticipants
          : converted
        )
          .map((p) => new Array(p.selectedNumber).fill(p))
          .flat()
          .map((p) => ({
            fullName: '',
            firstName: '',
            lastName: '',
            terms: false,
            price: p.price,
            priceTierName: p.name,
          })),
        notes: '',
        state: 'PENDING',
        bookingMode,
        listingId: listing.id,
        experienceId: experience?.id,
        additionalCustomQuestionResponses: {},
        sessionDateTime: uniqueSessions[0]?.startDateTime,
        sessionDuration: selectedDuration,
        timezone: listing?.location?.timezone,
        sessions: uniqueSessions,
        startDate: startDateCalendar?.format('YYYY-MM-DD'),
        endDate: endDateCalendar?.format('YYYY-MM-DD'),
      };
      onAddToCart(
        cartData,
        `/e/${experience.slug}/${
          isCartFeatureEnabled ? 'participants' : 'booking'
        }/{cartItemId}`,
        querySuffix,
      );
      return;
    }

    setCodeIsRequiredMsg(null);

    if (
      isSessionMultipleTiers(availablePriceTiers) &&
      totalSelectedParticipants < 1
    ) {
      setError('selectParticipantValidation', {
        type: 'manual',
        message: translate('selectParticipantValidation'),
      });
      setParticipantValidationMessage(translate('selectParticipantValidation'));
      return;
    }

    if (!data) return;
    const { noOfGuests, sessionDate, sessionTime } = data;

    if (noOfGuests) {
      dispatch(
        setTierParticipants({
          participants: availablePriceTiers,
          currentExperience: experience.id,
        }),
      );

      const selectedData = {
        ...selectedSession?.priceTiers[0],
        ...{ selectedNumber: noOfGuests.value },
      };

      setSelectedTierCustomers({
        selectedParticipants: [selectedData],
        currentExperience,
      });
    }
    const formattedSessionDate = formatDateForReq(
      isOneTime ? availableDates[0] : sessionDate,
    );

    const newSessionTime = isOneTime
      ? availableSessions[0]?.startTime
      : sessionTime;

    let validatedAccessCode = null;
    if (experience.isExclusive) {
      if (!accessCodeNumber) {
        setCodeIsRequiredMsg('Exclusive Code Is Required.');
        return;
      }
      const validateResponse = await dispatch(
        validateAccessCode({
          code: accessCodeNumber,
          brandId,
          experienceId: experience.id,
          isCartFeatureEnabled,
        }),
      );
      if (validateResponse.type === VALIDATE_ACCESS_CODE_FAILURE) {
        setCodeIsRequiredMsg('Invalid Code');
        return;
      }
      validatedAccessCode = validateResponse.payload?.accessCode ?? null;
    }

    const cartData = {
      participants: (isSessionMultipleTiers(availablePriceTiers)
        ? selectedParticipants
        : [
            {
              ...selectedSession?.priceTiers[0],
              ...{ selectedNumber: noOfGuests.value },
            },
          ]
      )
        .map((p) => new Array(p.selectedNumber).fill(p))
        .flat()
        .map((p) => ({
          fullName: '',
          firstName: '',
          lastName: '',
          terms: false,
          price: p.price,
          priceTierName: p.name,
        })),
      notes: '',
      state: 'PENDING',
      bookingMode,
      listingId: listing.id,
      experienceId: experience?.id,
      additionalCustomQuestionResponses: {},
      sessionDateTime: `${
        formattedDate ?? formattedSessionDate
      }T${newSessionTime}`,
      sessionDuration: selectedDuration,
      timezone: listing?.location?.timezone,
      ...(validatedAccessCode?.cartItemMetadata?.experienceIds?.includes(
        experience.id,
      ) && { accessCode: validatedAccessCode.code }),
      // TODO - to fix the private booking total price bug*, we can just add maxParticipantCountForPrivateBooking.
      // But we need to fix this bug also in the backend.
      // * (totalPrice = ticketPrice * listing.defaultParticipantsCount instead of selectedSession.supportedParticipantsCount)
    };

    const resourceOptions = await getResourceOptions(cartData.sessionDateTime);
    const participantsRoute = `/e/${experience.slug}/${
      isCartFeatureEnabled ? 'participants' : 'booking'
    }/{cartItemId}`;
    const nextStep = deriveNextBookingStep(cartData, {
      askParticipantsInfo,
      hasBookableAddOns:
        !!listing?.addOns?.length &&
        (await hasBookableAddOns(cartData.sessionDateTime)),
      hasCustomQuestions,
      hasResourceOptions: hasResourceOptionsForParticipants(
        cartData.participants,
        resourceOptions,
      ),
      participantsRoute,
    });
    if (nextStep.route === participantsRoute) {
      params.set(
        'skipParticipantsName',
        askParticipantsInfo ? 'false' : 'true',
      );
    }
    onAddToCart(nextStep.cartData, nextStep.route, params.toString());
  };

  const isNoSeatsAvailableForSession =
    !isSessionMultipleTiers(availablePriceTiers) &&
    availableSeatsForSession?.length === 0;

  const containsRemindersText =
    !!experience?.healthAndSafety &&
    experience?.healthAndSafety !== '<p><br></p>';

  const isDayHighlighted = (date) => {
    const isHighlighted =
      !calendarLoading &&
      availableDates?.find((availabilityDate) => {
        return moment(availabilityDate).isSame(date, 'day');
      });

    return isHighlighted;
  };

  const onNextMonthClick = async (todayDate) => {
    setCalendarLoading(true);
    let fromDate = todayDate;
    if (todayDate.isAfter(moment(), 'day')) {
      fromDate = todayDate.clone().startOf('month');
    }
    await getAvailableDatesHelper({
      from: fromDate,
      to: todayDate.clone().add(1, 'month').endOf('month'),
      isPrivate: isPrivateMode,
      shouldAppend: true,
      latest: String(searchParams.latest).toLowerCase() === 'true',
    });
    setCalendarLoading(false);
  };

  const onPrevMonthClick = async (todayDate) => {
    setCalendarLoading(true);
    let fromDate = todayDate;
    if (todayDate.isAfter(moment(), 'day')) {
      fromDate = todayDate.clone().startOf('month');
    }
    await getAvailableDatesHelper({
      from: fromDate,
      to: todayDate.clone().add(1, 'month').endOf('month'),
      isPrivate: isPrivateMode,
      shouldAppend: true,
      latest: String(searchParams.latest).toLowerCase() === 'true',
    });
    setCalendarLoading(false);
  };

  const onSessionDatePrevMonthClick = async (todayDate) => {
    setCalendarLoading(true);
    /* eslint no-unused-expressions: 'off' */
    // This next line would preselect the first day of the new month, even if no sessions are available on that day.
    // availabilityCalendarPrevButton.current?.click();
    let fromDate = todayDate;
    if (todayDate.isAfter(moment(), 'day')) {
      fromDate = todayDate.clone().startOf('month');
    }
    const toDate = todayDate.clone().add(1, 'month').endOf('month');
    setDateRange({
      startDateCalendar: fromDate,
      endDateCalendar: toDate,
    });
    await getAvailableDatesHelper({
      from: fromDate,
      to: toDate,
      isPrivate: isPrivateMode,
      shouldAppend: true,
      latest: String(searchParams.latest).toLowerCase() === 'true',
    });
    setCalendarLoading(false);
  };

  const onSessionDateNextMonthClick = async (todayDate) => {
    setCalendarLoading(true);
    /* eslint no-unused-expressions: 'off' */
    // This next line would preselect the first day of the new month, even if no sessions are available on that day.
    // availabilityCalendarNextButton.current?.click();
    let fromDate = todayDate;
    if (todayDate.isAfter(moment(), 'day')) {
      fromDate = todayDate.clone().startOf('month');
    }
    const toDate = todayDate.clone().add(1, 'month').endOf('month');
    setDateRange({
      startDateCalendar: fromDate,
      endDateCalendar: toDate,
    });
    await getAvailableDatesHelper({
      from: fromDate,
      to: toDate,
      isPrivate: isPrivateMode,
      shouldAppend: true,
      latest: String(searchParams.latest).toLowerCase() === 'true',
    });
    setCalendarLoading(false);
  };

  const onPrevDateRange = () => {
    const fromDate = moment(startDateCalendar)
      .clone()
      .subtract(2, 'month')
      .startOf('month');
    const toDate = fromDate.clone().add(1, 'month').endOf('month');
    setDateRange({
      startDateCalendar: fromDate,
      endDateCalendar: toDate,
    });

    getAvailableDatesHelper({
      from: fromDate,
      to: toDate,
      isPrivate: isPrivateMode,
      shouldAppend: true,
      latest: String(searchParams.latest).toLowerCase() === 'true',
    });
  };

  const onNextDateRange = () => {
    // Fetch the window around the probed next available date (see the
    // checkNextAvailableDate effect below), so gaps longer than the fetched
    // window are skipped over and dates far in the future stay reachable.
    // (WAY-14831)
    if (!nextAvailableDate) {
      return;
    }
    const fromDate = moment(nextAvailableDate);
    const toDate = fromDate
      .clone()
      .endOf('month')
      .add(1, 'month')
      .endOf('month');
    setDateRange({
      startDateCalendar: fromDate.clone().startOf('month'),
      endDateCalendar: toDate,
    });

    getAvailableDatesHelper({
      from: fromDate,
      to: toDate,
      isPrivate: isPrivateMode,
      shouldAppend: true,
      latest: String(searchParams.latest).toLowerCase() === 'true',
    });
  };

  // Keep `nextAvailableDate` in sync for open recurring experiences: after
  // each fetched window, probe whether any availability exists beyond it.
  // DateView only offers "see other dates" while a next date is confirmed,
  // and the click above fetches that exact date's window. (WAY-14831)
  useEffect(() => {
    if (!experience?.id || isBookable || !isRecurring || !fetchedUntilDate) {
      return;
    }
    dispatch(
      checkNextAvailableDate(experience.id, {
        from: formatDateForReq(moment(fetchedUntilDate).add(1, 'day')),
        isPrivate: isPrivateMode,
        latest: String(searchParams.latest).toLowerCase() === 'true',
      }),
    );
  }, [
    experience?.id,
    isBookable,
    isRecurring,
    fetchedUntilDate,
    isPrivateMode,
  ]);

  useEffect(() => {
    if (isSessionMultipleTiers(availablePriceTiers) && currentExperience) {
      setSelectedTierCustomers({
        selectedParticipants,
        currentExperience,
      });
    }
  }, [selectedParticipants]);

  const hostName = useMemo(() => {
    const host = experience.hostedBy;
    if (!host?.firstName || !host?.lastName) {
      return 'Host';
    }

    return `${host.firstName ?? ''} ${host.lastName ?? ''}`.trim();
  }, [experience?.hostedBy]);

  useEffect(() => {
    if (fetchSessionStatus === STATUS_PENDING || !selectedEventSession) return;

    const priceTiers = availablePriceTiers ?? availableSessions[0]?.priceTiers;
    const noOfGuests = getValues('noOfGuests');
    if (isSessionMultipleTiers(priceTiers)) {
      const countableParticipants = priceTiers?.map((priceLabel) => ({
        ...priceLabel,
        selectedNumber: 0,
      }));
      dispatch(
        setTierParticipants({
          participants: countableParticipants,
          currentExperience: experience.id,
        }),
      );
      return;
    }
    if (noOfGuests?.value > availableSeatsForSession?.length) {
      setSidebarFormValue('noOfGuests', { label: 1, value: 1 });
    }
  }, [experience, availableSessions, selectedEventSession, fetchSessionStatus]);

  useEffect(() => {
    dispatch(
      integrateSessionParticipants({
        session: availableSessions[0],
      }),
    );
  }, [availableSessions, experience]);

  useEffect(() => {
    if (fetchSessionStatus === STATUS_PENDING) {
      return;
    }
    const validSession = availableSessions?.find((sess) => {
      return (
        sess?.startTime === selectedStartTime &&
        sess.duration === selectedDuration
      );
    });

    setSelectedEventSession(validSession);
    dispatch(integrateSessionParticipants({ session: validSession }));
  }, [
    selectedStartTime,
    selectedDuration,
    availableSessions,
    fetchSessionStatus,
  ]);

  useEffect(() => {
    if (selectedSessionDateAvailabilityCalendar) {
      dispatchAvailableSessionsHelper(selectedSessionDateAvailabilityCalendar, {
        isPrivate: isPrivateMode,
        latest: String(searchParams.latest).toLowerCase() === 'true',
      });
    }
    setSidebarFormValue('sessionTime', null);
  }, [selectedSessionDateAvailabilityCalendar, isExperienceFetchSuccess]);

  useEffect(() => {
    if (firstAvailableDate) {
      setSelectedSessionDateAvailabilityCalendar(moment(firstAvailableDate));
      setSidebarFormValue('sessionDate', moment(firstAvailableDate));
      if (!isRequestToBookEnabled) {
        setDateRange({
          startDateCalendar: moment(firstAvailableDate).startOf('month'),
          endDateCalendar: moment(firstAvailableDate)
            .endOf('month')
            .add(1, 'month')
            .endOf('month'),
        });
      }
      let fromDate = moment(firstAvailableDate).startOf('month');
      if (fromDate.isSame(moment(), 'month')) {
        fromDate = moment();
      }
      getAvailableDatesHelper({
        from: fromDate,
        to: moment(firstAvailableDate)
          .endOf('month')
          .add(1, 'month')
          .endOf('month'),
        isPrivate: isPrivateMode,
        shouldAppend: true,
        latest: String(searchParams.latest).toLowerCase() === 'true',
      });
    }
    if (
      (fetchFirstAvailabilityStatus === STATUS_SUCCESS ||
        fetchFirstAvailabilityStatus === STATUS_FAILURE) &&
      !firstAvailableDate
    ) {
      !isRequestToBookEnabled &&
        setDateRange({
          startDateCalendar: moment().startOf('month'),
          endDateCalendar: moment()
            .endOf('month')
            .add(1, 'month')
            .endOf('month'),
        });
      getAvailableDatesHelper({
        from: moment(),
        to: moment().endOf('month').add(1, 'month').endOf('month'),
        isPrivate: isPrivateMode,
        shouldAppend: true,
        latest: String(searchParams.latest).toLowerCase() === 'true',
      });
    }
  }, [firstAvailableDate, fetchFirstAvailabilityStatus]);

  useEffect(() => {
    const isValidDefault = (session) =>
      !session.isDisabled && session.duration === selectedDuration;

    // Honor a ?sessionTime=HH:mm deep link from the schedule view whenever that slot is bookable
    // for the selected duration. This runs BEFORE the "keep current selection" check below and is
    // re-applied on every settle (not one-shot), so it wins even after a transient/other-day
    // session list momentarily stranded the selection on the first slot — the list settles
    // asynchronously across several availability fetches (WAY-16106 flaky selection). It stops
    // overriding once the user manually picks a time (userSelectedStartTimeRef).
    const urlSessionTime = searchParams.sessionTime;
    const urlMatch =
      !userSelectedStartTimeRef.current && urlSessionTime
        ? experienceStartTimes?.find(
            (session) =>
              isValidDefault(session) &&
              String(session.startTime).slice(0, 5) ===
                String(urlSessionTime).slice(0, 5),
          )
        : undefined;
    if (urlMatch) {
      setSelectedStartTime(urlMatch.startTime);
      setSidebarFormValue('sessionTime', urlMatch.startTime);
      return;
    }

    const isValidSelectedStartTime = experienceStartTimes?.find((session) => {
      return (
        !session.isDisabled &&
        session.duration === selectedDuration &&
        session.startTime === selectedStartTime
      );
    });
    if (isValidSelectedStartTime) {
      /* If duration is changed, but a valid selectedStartTime corresponds correctly to that duration */
      /* then keep the selectedStartTime state as it is, dont update */
      return;
    }

    /*
      Give default state to selectedStartTime if it is null,
      Or if a session startTime doesn't exist for the selected duration,
      reset it with the first available valid startTime
    */
    const firstValidSession = experienceStartTimes?.find(isValidDefault);

    setSelectedStartTime(firstValidSession?.startTime || null);
    setSidebarFormValue('sessionTime', firstValidSession?.startTime);
  }, [selectedDuration, experienceStartTimes]);

  const filterSearchParams = (params) => {
    return omit(params, ['noOfGuests', 'sessionDate', 'mode']);
  };

  const onModalClosure = () => {
    setIsViewOpen(false);
    setShowModal(false);
  };

  const onLinkToPreviousPage = () => {
    if (searchParams?.previous === '/calendar') {
      customReplace({
        pathname: `/calendar`,
        search: serializeParams(filterSearchParams(searchParams)),
      });
      return;
    }
    // A catalog page sends its visitors here with `previous=/catalogs/<id>`, the way the schedule
    // view does with `/calendar`; back returns to that catalog, not to the homepage.
    if (
      typeof searchParams?.previous === 'string' &&
      searchParams.previous.startsWith('/catalogs/')
    ) {
      customReplace({
        pathname: searchParams.previous,
        search: serializeParams(filterSearchParams(searchParams)),
        unset: ['previous'],
      });
      return;
    }
    customReplace({
      pathname: `/experiences`,
      search: serializeParams(filterSearchParams(searchParams)),
    });
  };

  const onDropdownToggle = (e) => {
    if (isDropdownVisible && !isEqual(e.target.id, 'noOfGuests')) {
      setIsDropdownVisible(false);
    }
  };

  const onTierDropdownClick = (e) => {
    e.stopPropagation();
  };

  const isFetchingSessionTiers =
    (fetchSessionStatus === STATUS_IDLE ||
      fetchSessionStatus === STATUS_PENDING) &&
    fetchFirstAvailabilityStatus !== STATUS_FAILURE;

  useEffect(() => {
    if (durations.length) {
      setSelectedDuration(durations[0]?.value);
    }
  }, [durations]);

  const isFlexibleDurationEnabled = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.FLEXIBLE_DURATION,
  );

  const carouselMedias = formatListingMedia(experience?.medias).map(
    (media, index) => ({
      ...media,
      alt: media.altText || `${experience?.title} photo ${index + 1}`,
    }),
  );

  const hasBlockedDateInRange = (startDate, endDate) => {
    const day = startDate.clone();
    while (day.isSameOrBefore(endDate, 'day')) {
      if (!isDayHighlighted(day)) {
        return true;
      }
      day.add(1, 'day');
    }
    return false;
  };

  const { sessions } = useAppState((state) => state.experience);

  const handleOnDateChange = async ({ startDate, endDate }) => {
    if (startDate && endDate && hasBlockedDateInRange(startDate, endDate)) {
      console.warn('Range includes blocked dates');
      return;
    }

    // Clear validation error when user changes dates
    setDateRangeValidationMessage(null);

    setDateRange({
      startDateCalendar: startDate,
      endDateCalendar: endDate,
    });

    if (!startDate || !endDate) return;
    if (!experience?.id) return;

    dispatch(
      getDateRangeAvailableSessions(
        brandId,
        experience.id,
        startDate.toDate(),
        endDate.toDate(),
        cartItems,
      ),
    );
  };

  const bookButtonLabel =
    limitedTime?.state === TicketSalesState.NOT_YET_OPEN
      ? translate('comingSoon')
      : experience.customCallToAction || translate('bookExperience');

  const getBookButtonAnalyticData = useCallback(() => {
    const totalPrice = isRequestToBookEnabled
      ? getTotalPriceExcludingLast(sessions)
      : getSessionPrice(
          selectedEventSession ?? sessions[0],
          experience,
          isPrivateMode,
          showAdvertisedPrice,
        );

    const analyticData = {
      [ANALYTICS_PROPERTY.BookingRequestTotalPrice]:
        totalPrice.toString() ?? undefined,
      [ANALYTICS_PROPERTY.BookingRequestStartDate]:
        startDateCalendar?.format('MM-DD-YYYY') ?? undefined,
      [ANALYTICS_PROPERTY.BookingRequestEndDate]:
        endDateCalendar?.format('MM-DD-YYYY') ?? undefined,
    };

    analyticsManager.setAppData(analyticData);

    return analyticData;
  }, [
    isRequestToBookEnabled,
    selectedEventSession,
    sessions,
    experience,
    isPrivateMode,
    showAdvertisedPrice,
    startDateCalendar,
    endDateCalendar,
  ]);

  // For Hyatt, we need to check if the parent organization is Hyatt for price disclaimer
  // This could be extended to other brands in the future

  /*
  const { isParentOrganization } = useOrganizations(
    brandId,
    getHyattOrganizationId(),
  );

  const hasPriceDisclaimer = isParentOrganization || false;
  /*


  /* eslint jsx-a11y/label-has-associated-control: 'off' */

  // Show 404 if:
  // 1. Experience fetch failed AND no experience.id (experience not found)
  // 2. Has real errors (exchangeIdError or listingError)
  // 3. Finished loading AND exchangeIds returned empty array (experience not found)
  // 4. Finished loading AND valid listingId exists BUT no listing found
  const shouldShow404 =
    (fetchExperienceStatus === STATUS_FAILURE && !experience.id) ||
    (fetchExperienceStatus === STATUS_SUCCESS &&
      !isLoadingListing &&
      (exchangeIdError ||
        listingError ||
        (exchangeIds && exchangeIds.length === 0) ||
        (!!listingId && !listing)));
  const isListingContextLoading =
    isLoadingListing || (!listing && !listingError);

  return (
    <div
      style={isViewOpen ? { overflow: 'hidden', height: '0' } : null}
      id="experience-view"
      key={i18n.language}
    >
      <EventMeta
        {...experience}
        title={listing?.title || experience.title}
        slug={experience.slug || ''}
      />
      {isDropdownVisible ? (
        <SC.TierOverlay
          className="experience-view__tier-overlay"
          onClick={() => setIsDropdownVisible(false)}
        />
      ) : null}
      <Wrapper>
        {String(searchParams?.latest).toLowerCase() !== 'true' && (
          <NavigationHeader
            onGoBack={onLinkToPreviousPage}
            preventDefaultBack
            className="experience-view__experience-navigation"
          />
        )}
        {shouldShow404 ? (
          <NotFoundExperience />
        ) : (
          <SC.ExperiencesWrapper>
            <SC.ExperiencesHeader>
              <ErrorBoundary>
                <ExperienceHeader
                  experience={experience}
                  isLoadingBrand={!!promoterBrand?.isLoading}
                  isLoadingListing={isLoadingListing}
                  listing={listing}
                  promoterBrandName={promoterBrand?.brand?.name}
                  hyattEligible={isHyattEligible(eligibleProgram)}
                />
              </ErrorBoundary>
              <ErrorBoundary>
                <MediaCarouselWrapper
                  style={{
                    overflow: isMobile ? 'hidden' : 'visible',
                  }}
                >
                  {carouselMedias.length > 0 && (
                    <MediaCarousel
                      medias={carouselMedias}
                      brandFlagName={
                        eligibleProgram &&
                        Object.values(eligibleProgram)[0]?.payEligible
                          ? Object.values(eligibleProgram)[0].org
                          : null
                      }
                    />
                  )}
                </MediaCarouselWrapper>
              </ErrorBoundary>
              {isBookable && isMobile && (
                <ExperienceBookingSidebar
                  experience={experience}
                  listing={listing}
                  isLoadingListing={isListingContextLoading}
                  isLoadingCustomQuestions={isLoadingCustomQuestions}
                  isRecurring={isRecurring}
                  isOneTime={isOneTime}
                  hideWidgets={hideWidgets}
                  isRequestToBookEnabled={isRequestToBookEnabled}
                  isMobile={isMobile}
                  searchParams={searchParams}
                  control={control}
                  errors={errors}
                  handleSubmit={handleSubmit}
                  clearErrors={clearErrors}
                  selectedEventSession={selectedEventSession}
                  isPrivateMode={isPrivateMode}
                  availablePriceTiers={availablePriceTiers}
                  startDateCalendar={startDateCalendar}
                  endDateCalendar={endDateCalendar}
                  selectedSessionDateAvailabilityCalendar={
                    selectedSessionDateAvailabilityCalendar
                  }
                  setSelectedSessionDateAvailabilityCalendar={
                    setSelectedSessionDateAvailabilityCalendar
                  }
                  isDropdownVisible={isDropdownVisible}
                  setIsDropdownVisible={setIsDropdownVisible}
                  availableSeatsForSession={availableSeatsForSession}
                  totalSelectedParticipants={totalSelectedParticipants}
                  isFetchingSessionTiers={isFetchingSessionTiers}
                  setSinglePriceTierCount={setSinglePriceTierCount}
                  cartExperience={cartExperience}
                  participantValidationMessage={participantValidationMessage}
                  setParticipantValidationMessage={
                    setParticipantValidationMessage
                  }
                  dateRangeValidationMessage={dateRangeValidationMessage}
                  setDateRangeValidationMessage={setDateRangeValidationMessage}
                  durations={durations}
                  selectedDuration={selectedDuration}
                  setSelectedDuration={setSelectedDuration}
                  experienceStartTimes={experienceStartTimes}
                  isUnavailable={isUnavailable}
                  selectedStartTime={selectedStartTime}
                  setSelectedStartTime={handleSelectStartTime}
                  accessCodeNumber={accessCodeNumber}
                  setAccessCodeNumber={setAccessCodeNumber}
                  availableDates={availableDates}
                  codeIsRequiredMsg={codeIsRequiredMsg}
                  containsRemindersText={containsRemindersText}
                  isNoSeatsAvailableForSession={isNoSeatsAvailableForSession}
                  isSoldOut={isSoldOut}
                  isWithinCuttoff={isWithinCuttoff}
                  validateAccessCodeStatus={validateAccessCodeStatus}
                  bookButtonLabel={bookButtonLabel}
                  limitedTime={limitedTime}
                  calendarLoading={calendarLoading}
                  setCalendarLoading={setCalendarLoading}
                  isDayHighlighted={isDayHighlighted}
                  handleOnDateChange={handleOnDateChange}
                  onPrevMonthClick={onPrevMonthClick}
                  onNextMonthClick={onNextMonthClick}
                  onSessionDatePrevMonthClick={onSessionDatePrevMonthClick}
                  onSessionDateNextMonthClick={onSessionDateNextMonthClick}
                  onPrevDateRange={onPrevDateRange}
                  onNextDateRange={onNextDateRange}
                  onDropdownToggle={onDropdownToggle}
                  onTierDropdownClick={onTierDropdownClick}
                  handlePrivateCheckboxChange={handlePrivateCheckboxChange}
                  handleBooking={handleBooking}
                  getBookButtonAnalyticData={getBookButtonAnalyticData}
                  type={type}
                  isFlexibleDurationEnabled={isFlexibleDurationEnabled}
                  fetchFirstAvailabilityStatus={fetchFirstAvailabilityStatus}
                />
              )}
            </SC.ExperiencesHeader>
            <SC.ExperiencesBody>
              <SC.ExperiencesBodyContent loading={experience.loading}>
                {!experience.loading ? (
                  <SC.Title4
                    as="h2"
                    className="experience-view__experience-section-title"
                  >
                    {translate('details')}
                  </SC.Title4>
                ) : (
                  <SkeletonLine translucent />
                )}
                {!experience.loading || !isLoadingListing ? (
                  <SC.Padding8>
                    <TruncatedText
                      text={listing?.description || ''}
                      isRichText
                      mode="inline"
                    />
                  </SC.Padding8>
                ) : (
                  <SkeletonLine translucent />
                )}
                {experience.loading || isLoadingListing ? (
                  <SkeletonLine translucent />
                ) : (
                  !!experienceDescImage && (
                    <SC.Padding8>
                      <ExperienceDescImage
                        uriObject={experienceDescImage}
                        altText={experience?.title}
                      />
                    </SC.Padding8>
                  )
                )}

                <ExperienceHost
                  experience={experience}
                  hostName={hostName}
                  isHostLed={isHostLed}
                  isLoadingListing={isLoadingListing}
                  isSquare={isSquare}
                  onImgLoad={onImgLoad}
                  userProfileImage={userProfileImage}
                />
                {experience?.providedItems?.length > 0 && (
                  <SC.ExperienceInclude>
                    {!experience.loading ? (
                      <SC.Title4
                        as="h3"
                        className="experience-view__experience-section-title"
                      >
                        {isRequestToBookEnabled
                          ? translate('amenities')
                          : translate('whatsIncluded')}
                      </SC.Title4>
                    ) : (
                      <SkeletonLine translucent />
                    )}
                    <SC.ExperienceIncludeListWrap>
                      {!experience.loading ? (
                        <SC.ExperienceIncludeList>
                          {experience.providedItems.map((providedItem) => (
                            <SC.ListButton
                              className="experience-view__reminder-item"
                              key={`providedItem-${providedItem}`}
                            >
                              {providedItem}
                            </SC.ListButton>
                          ))}
                        </SC.ExperienceIncludeList>
                      ) : (
                        <SkeletonLine translucent />
                      )}
                    </SC.ExperienceIncludeListWrap>
                  </SC.ExperienceInclude>
                )}
                <ErrorBoundary>
                  {isRequestToBookEnabled ? (
                    <RangeCalendarAvailability
                      endDateCalendar={endDateCalendar}
                      experience={experience}
                      handleOnDateChange={handleOnDateChange}
                      isBookable={isBookable}
                      isDayHighlighted={isDayHighlighted}
                      isRecurring={isRecurring}
                      startDateCalendar={startDateCalendar}
                      onNextMonthClick={onNextMonthClick}
                      onPrevMonthClick={onPrevMonthClick}
                      isNarrowView={isNarrowView}
                    />
                  ) : (
                    <CalendarAvailability
                      experience={experience}
                      isDayHighlighted={isDayHighlighted}
                      isNarrowView={isNarrowView}
                      onNextMonthClick={onNextMonthClick}
                      onPrevMonthClick={onPrevMonthClick}
                      searchAvailabilityStatus={searchAvailabilityStatus}
                      setSidebarFormValue={setSidebarFormValue}
                      startDateCalendar={startDateCalendar}
                      isCalendarLoading={calendarLoading}
                      firstAvailableDate={firstAvailableDate}
                      setSelectedSessionDateAvailabilityCalendar={
                        setSelectedSessionDateAvailabilityCalendar
                      }
                    />
                  )}
                </ErrorBoundary>
                <ExperienceLocation
                  className="experience-view__experience-section-title"
                  loading={experience.loading}
                  location={experience.location}
                />
                <ExperienceCancellationPolicy
                  className="experience-view__experience-section-title"
                  cancellationPolicy={cancellationPolicy}
                  isLoading={isLoadingCancellationPolicy}
                />
                <ErrorBoundary>
                  <ToggleFeature featureId="reviews">
                    <ReviewsList
                      {...experience}
                      loading={experience.loading ?? false}
                    />
                  </ToggleFeature>
                </ErrorBoundary>
              </SC.ExperiencesBodyContent>

              <ErrorBoundary>
                <SC.SidebarColumnStack>
                  {limitedTime?.static && (
                    <LimitedTimeContainer>
                      <LimitedTimeMessage
                        label={limitedTime.static.full}
                        highlightPart={limitedTime.static.highlight}
                      />
                    </LimitedTimeContainer>
                  )}

                  {promoterBrand?.data?.name && (
                    <PromotersCard
                      promoterBrand={promoterBrand.data}
                      withBorder
                    />
                  )}
                  {isBookable && !isMobile && (
                    <ExperienceBookingSidebar
                      experience={experience}
                      listing={listing}
                      isLoadingListing={isListingContextLoading}
                      isLoadingCustomQuestions={isLoadingCustomQuestions}
                      isRecurring={isRecurring}
                      isOneTime={isOneTime}
                      hideWidgets={hideWidgets}
                      isRequestToBookEnabled={isRequestToBookEnabled}
                      isMobile={isMobile}
                      searchParams={searchParams}
                      control={control}
                      errors={errors}
                      handleSubmit={handleSubmit}
                      clearErrors={clearErrors}
                      selectedEventSession={selectedEventSession}
                      isPrivateMode={isPrivateMode}
                      availablePriceTiers={availablePriceTiers}
                      startDateCalendar={startDateCalendar}
                      endDateCalendar={endDateCalendar}
                      selectedSessionDateAvailabilityCalendar={
                        selectedSessionDateAvailabilityCalendar
                      }
                      setSelectedSessionDateAvailabilityCalendar={
                        setSelectedSessionDateAvailabilityCalendar
                      }
                      isDropdownVisible={isDropdownVisible}
                      setIsDropdownVisible={setIsDropdownVisible}
                      availableSeatsForSession={availableSeatsForSession}
                      totalSelectedParticipants={totalSelectedParticipants}
                      isFetchingSessionTiers={isFetchingSessionTiers}
                      setSinglePriceTierCount={setSinglePriceTierCount}
                      cartExperience={cartExperience}
                      participantValidationMessage={
                        participantValidationMessage
                      }
                      setParticipantValidationMessage={
                        setParticipantValidationMessage
                      }
                      dateRangeValidationMessage={dateRangeValidationMessage}
                      setDateRangeValidationMessage={
                        setDateRangeValidationMessage
                      }
                      durations={durations}
                      selectedDuration={selectedDuration}
                      setSelectedDuration={setSelectedDuration}
                      experienceStartTimes={experienceStartTimes}
                      isUnavailable={isUnavailable}
                      selectedStartTime={selectedStartTime}
                      setSelectedStartTime={handleSelectStartTime}
                      accessCodeNumber={accessCodeNumber}
                      setAccessCodeNumber={setAccessCodeNumber}
                      availableDates={availableDates}
                      codeIsRequiredMsg={codeIsRequiredMsg}
                      containsRemindersText={containsRemindersText}
                      isNoSeatsAvailableForSession={
                        isNoSeatsAvailableForSession
                      }
                      isSoldOut={isSoldOut}
                      isWithinCuttoff={isWithinCuttoff}
                      validateAccessCodeStatus={validateAccessCodeStatus}
                      bookButtonLabel={bookButtonLabel}
                      limitedTime={limitedTime}
                      calendarLoading={calendarLoading}
                      setCalendarLoading={setCalendarLoading}
                      isDayHighlighted={isDayHighlighted}
                      handleOnDateChange={handleOnDateChange}
                      onPrevMonthClick={onPrevMonthClick}
                      onNextMonthClick={onNextMonthClick}
                      onSessionDatePrevMonthClick={onSessionDatePrevMonthClick}
                      onSessionDateNextMonthClick={onSessionDateNextMonthClick}
                      onPrevDateRange={onPrevDateRange}
                      onNextDateRange={onNextDateRange}
                      onDropdownToggle={onDropdownToggle}
                      onTierDropdownClick={onTierDropdownClick}
                      handlePrivateCheckboxChange={handlePrivateCheckboxChange}
                      handleBooking={handleBooking}
                      getBookButtonAnalyticData={getBookButtonAnalyticData}
                      type={type}
                      isFlexibleDurationEnabled={isFlexibleDurationEnabled}
                      fetchFirstAvailabilityStatus={
                        fetchFirstAvailabilityStatus
                      }
                    />
                  )}
                  <OpenExperienceView
                    availableDates={availableDates}
                    containsRemindersText={containsRemindersText}
                    experience={experience}
                    isBookable={isBookable}
                    onNextDateRange={onNextDateRange}
                    selectedEventSession={selectedEventSession}
                    isPrivateMode={isPrivateMode}
                  />
                </SC.SidebarColumnStack>
              </ErrorBoundary>
            </SC.ExperiencesBody>
          </SC.ExperiencesWrapper>
        )}

        {showModal && (
          <PictureModal>
            <ModalGallery closeModal={onModalClosure} experience={experience} />
          </PictureModal>
        )}
      </Wrapper>
    </div>
  );
};

const MediaCarouselWrapper = styled.div`
  aspect-ratio: 16 / 9;

  @media (min-width: 768px) {
    max-width: 630px;
    min-height: 350px;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

export default React.memo(ExperienceView);
